/**
 * Shared Monaco editor setup for ScadBlock.
 *
 * Call registerOpenSCADLanguage once after Monaco loads to register the
 * OpenSCAD language token provider and language configuration.
 * Call definePlaygroundThemes to register the dark/light custom themes.
 *
 * Both functions are idempotent — safe to call multiple times on the same
 * Monaco instance.
 */

import type { Monaco } from '@monaco-editor/react';
import {
  DARK_BG,
  DARK_FG,
  DARK_LINE_HIGHLIGHT,
  DARK_LINE_NUMBER,
  DARK_SELECTION,
  DARK_SELECTION_INACTIVE,
  DARK_BRACKET_HIGHLIGHT,
  DARK_COMMENT,
  DARK_KEYWORD,
  DARK_IDENTIFIER,
  DARK_STRING,
  DARK_NUMBER,
  DARK_DELIMITER,
  DARK_OPERATOR,
  DARK_BRACKET,
  LIGHT_BG,
  LIGHT_FG,
  LIGHT_LINE_HIGHLIGHT,
  LIGHT_LINE_NUMBER,
  LIGHT_SELECTION,
  LIGHT_SELECTION_INACTIVE,
  LIGHT_COMMENT,
  LIGHT_KEYWORD,
  LIGHT_IDENTIFIER,
  LIGHT_STRING,
  LIGHT_NUMBER,
  LIGHT_DELIMITER,
  LIGHT_OPERATOR,
} from '@/lib/syntaxTheme';

// Monaco token rules require the hex color without the leading '#'
const hex = (color: string) => color.replace(/^#/, '');

// ---------------------------------------------------------------------------
// Themes
// ---------------------------------------------------------------------------

export function definePlaygroundThemes(monaco: Monaco): void {
  monaco.editor.defineTheme('playground-dark', {
    base: 'vs-dark',
    inherit: true,
    rules: [
      { token: 'comment', foreground: hex(DARK_COMMENT) },
      { token: 'keyword', foreground: hex(DARK_KEYWORD) },
      { token: 'identifier', foreground: hex(DARK_IDENTIFIER) },
      { token: 'string', foreground: hex(DARK_STRING) },
      { token: 'number', foreground: hex(DARK_NUMBER) },
      { token: 'delimiter', foreground: hex(DARK_DELIMITER) },
      { token: 'operator', foreground: hex(DARK_OPERATOR) },
      { token: 'bracket', foreground: hex(DARK_BRACKET) },
    ],
    colors: {
      'editor.background': DARK_BG,
      'editor.foreground': DARK_FG,
      'editor.lineHighlightBackground': DARK_LINE_HIGHLIGHT,
      'editorLineNumber.foreground': DARK_LINE_NUMBER,
      'editor.selectionBackground': DARK_SELECTION,
      'editor.inactiveSelectionBackground': DARK_SELECTION_INACTIVE,
      'editorBracketHighlight.foreground1': DARK_BRACKET_HIGHLIGHT,
      'editorBracketHighlight.foreground2': DARK_BRACKET_HIGHLIGHT,
      'editorBracketHighlight.foreground3': DARK_BRACKET_HIGHLIGHT,
    },
  });

  monaco.editor.defineTheme('playground-light', {
    base: 'vs',
    inherit: true,
    rules: [
      { token: 'comment', foreground: hex(LIGHT_COMMENT) },
      { token: 'keyword', foreground: hex(LIGHT_KEYWORD) },
      { token: 'identifier', foreground: hex(LIGHT_IDENTIFIER) },
      { token: 'string', foreground: hex(LIGHT_STRING) },
      { token: 'number', foreground: hex(LIGHT_NUMBER) },
      { token: 'delimiter', foreground: hex(LIGHT_DELIMITER) },
      { token: 'operator', foreground: hex(LIGHT_OPERATOR) },
    ],
    colors: {
      'editor.background': LIGHT_BG,
      'editor.foreground': LIGHT_FG,
      'editor.lineHighlightBackground': LIGHT_LINE_HIGHLIGHT,
      'editorLineNumber.foreground': LIGHT_LINE_NUMBER,
      'editor.selectionBackground': LIGHT_SELECTION,
      'editor.inactiveSelectionBackground': LIGHT_SELECTION_INACTIVE,
    },
  });
}

// ---------------------------------------------------------------------------
// Language
// ---------------------------------------------------------------------------

export function registerOpenSCADLanguage(monaco: Monaco): void {
  const existing = monaco.languages.getLanguages();
  if (!existing.some((l: { id: string }) => l.id === 'openscad')) {
    monaco.languages.register({ id: 'openscad' });
  }

  monaco.languages.setMonarchTokensProvider('openscad', {
    brackets: [
      { open: '{', close: '}', token: 'delimiter.curly' },
      { open: '[', close: ']', token: 'delimiter.square' },
      { open: '(', close: ')', token: 'delimiter.parenthesis' },
    ],
    tokenizer: {
      root: [
        [/\/\*/, 'comment', '@comment'],
        [/\/\/.*$/, 'comment'],
        [
          /(module|function|if|else|for|intersection|union|difference|translate|rotate|scale|resize|mirror|multmatrix|color|material|material_def|minkowski|hull|cylinder|cube|sphere|circle|square|polygon|polyhedron|text|surface|import|echo|use|include|render|projection|linear_extrude|rotate_extrude|offset)(?=\()/,
          'keyword',
        ],
        [/(true|false|undef)/, 'keyword'],
        [/[a-zA-Z_$]\w*/, { cases: { '@default': 'identifier' } }],
        [/[{}()[\]]/, '@brackets'],
        [/[;,.]/, 'delimiter'],
        [/@symbols/, { cases: { '@operators': 'operator', '@default': '' } }],
        [/\d*\.\d+([eE][-+]?\d+)?/, 'number.float'],
        [/0[xX][0-9a-fA-F]+/, 'number.hex'],
        [/\d+/, 'number'],
        [/"([^"\\]|\\.)*$/, 'string.invalid'],
        [/"/, { token: 'string.quote', bracket: '@open', next: '@string' }],
      ],
      comment: [
        [/[^/*]+/, 'comment'],
        [/\*\//, 'comment', '@pop'],
        [/[/*]/, 'comment'],
      ],
      string: [
        [/[^\\"]+/, 'string'],
        [/\\./, 'string.escape'],
        [/"/, { token: 'string.quote', bracket: '@close', next: '@pop' }],
      ],
    },
    keywords: [
      'module',
      'function',
      'if',
      'else',
      'for',
      'intersection',
      'union',
      'difference',
      'translate',
      'rotate',
      'scale',
      'resize',
      'mirror',
      'multmatrix',
      'color',
      'material',
      'material_def',
      'minkowski',
      'hull',
      'cylinder',
      'cube',
      'sphere',
      'circle',
      'square',
      'polygon',
      'polyhedron',
      'text',
      'surface',
      'import',
      'echo',
      'use',
      'include',
      'render',
      'projection',
      'linear_extrude',
      'rotate_extrude',
      'offset',
      '$fn',
      '$fa',
      '$fs',
      'true',
      'false',
      'undef',
    ],
    operators: [
      '=',
      '>',
      '<',
      '!',
      '~',
      '?',
      ':',
      '==',
      '<=',
      '>=',
      '!=',
      '&&',
      '||',
      '++',
      '--',
      '+',
      '-',
      '*',
      '/',
      '&',
      '|',
      '^',
      '%',
    ],
    symbols: /[=><!~?:&|+\-*/^%]+/,
  });

  monaco.languages.setLanguageConfiguration('openscad', {
    comments: { lineComment: '//', blockComment: ['/*', '*/'] },
    brackets: [
      ['{', '}'],
      ['[', ']'],
      ['(', ')'],
    ],
    autoClosingPairs: [
      { open: '{', close: '}' },
      { open: '[', close: ']' },
      { open: '(', close: ')' },
      { open: '"', close: '"' },
    ],
    surroundingPairs: [
      { open: '{', close: '}' },
      { open: '[', close: ']' },
      { open: '(', close: ')' },
      { open: '"', close: '"' },
    ],
  });
}
